import { createSelector } from 'reselect';
import { manualInitiationMenu, batchInitiationMenu } from './serviceInitReducer';
import { SI_ADDINITIATION_VIEWTYPES, SI_INITIATION_TYPES, SI_MENU_PAGESTATES } from 'src/constants/constants';
import { checkFields } from 'src/helpers';

const getAllState = state => state;
const serviceInitState = state => state.serviceInit;
export const getServiceInitState = createSelector([serviceInitState], serviceInit => serviceInit);

export const checkMenuVisible = createSelector([getServiceInitState], data => {
  const doNotShowMenuViews = [SI_ADDINITIATION_VIEWTYPES.SELECT_SERVICETYPE, SI_ADDINITIATION_VIEWTYPES.BATCH_UPLOAD];
  const isMenuNotVisible = doNotShowMenuViews.includes(data.addInitiation.viewType);
  return isMenuNotVisible ? false : true;
});

export const getCurrentMenu = createSelector([getServiceInitState], data => {
  const { addInitiation } = data;
  const { currentMenuPage, menu } = addInitiation;
  return menu[currentMenuPage];
});

export const checkOrganizerCreated = createSelector([getServiceInitState], data => {
  const { addInitiation } = data;

  if (addInitiation.organizerInfo && addInitiation.organizerInfo.assigneeServicePlanGlobalId !== undefined) return true;
  else return false;
});

export const checkNextEnabled = createSelector([getServiceInitState, getAllState], (data, allState) => {
  const { isNextEnabled } = data;
  let isEnabled = checkRequiredFields(allState) && isNextEnabled;
  return isEnabled;
});

export const checkDraftEnabled = createSelector([getServiceInitState, getAllState], (data, allState) => {
  const { addInitiation } = data;
  let isDraftEnabled = addInitiation.organizerInfo && addInitiation.organizerInfo.organizerInstanceId ? true : false;

  /*
   * If a user has finished filling out the Applicant details page they should be allowed to save draft.
   * Although we can not save until a organizerId has been generated so the following requests need to run in *sequence*
   *  
   * 1) apiRequest to createNewEmployee 
   * 2) apiRequest to createOrganizer
   *
   */

  if (addInitiation.viewType === SI_ADDINITIATION_VIEWTYPES.ADD_APPLICANT) {
    isDraftEnabled = checkRequiredFields(allState);
  }

  if (addInitiation.initiationType === SI_INITIATION_TYPES.BATCH_INITIATION) {
    isDraftEnabled = false;
  }

  return isDraftEnabled;
});

export const checkSubmitEnabled = createSelector([getServiceInitState], data => {
  const { addInitiation } = data;
  const { currentMenuPage, dynamicFormValues, menu } = addInitiation;

  let isSubmitEnabled = true;
  const isMenuComplete = status => status === SI_MENU_PAGESTATES.COMPLETE || status === SI_MENU_PAGESTATES.CURRENT_COMPLETE;

  menu.forEach((menuItem, menuIndex) => {
    if (
      (!isMenuComplete(menuItem.pageState) && menuIndex !== currentMenuPage) ||
      (menuIndex === currentMenuPage && !dynamicFormValues.isFormComplete)
    )
      isSubmitEnabled = false;
  });

  return isSubmitEnabled;
});

export const checkRequiredFields = createSelector([getServiceInitState], data => {
  const { addInitiation } = data;
  const { employee } = addInitiation;

  let isRequiredFilled = true;

  switch (addInitiation.viewType) {
    case SI_ADDINITIATION_VIEWTYPES.SELECT_SERVICETYPE:
      let requiredFields = ['engagement', 'initiationType'];

      if (addInitiation.initiationType === SI_INITIATION_TYPES.MANUAL_INITIATION) {
        requiredFields = requiredFields.concat(['serviceType', 'country']);
      }

      isRequiredFilled = checkFields(addInitiation, requiredFields);

      break;

    case SI_ADDINITIATION_VIEWTYPES.ADD_APPLICANT:
      /*
       * Two paths:
       *   1) Either a user has already selected an employee and we have logged the employeeId API response to `employee`
       *   2) User is filling out an Applicant details form and has filled out all required fields. This should enable
       *      the right next button so they can not only continue but create the new employee record.
       *
       */

      if (employee && employee.employeeId !== undefined) {
        isRequiredFilled = true;
      } else {
        const fields = ['firstName', 'lastName', 'emailAddress', 'dateOfBirth', 'contactNumber', 'relationshipStatus', 'employeeCode'];
        isRequiredFilled = checkFields(employee, fields);
      }

      break;

    default:
      break;
  }

  return isRequiredFilled;
});

export const deriveNewMenuAndProgress = (menus, markMenusComplete = []) =>
  createSelector([getServiceInitState], data => {
    const { addInitiation } = data;

    const staticMenuSize =
      addInitiation.initiationType === SI_INITIATION_TYPES.BATCH_INITIATION ? batchInitiationMenu.length : manualInitiationMenu.length;

    let menusComplete = 0;
    const isMenuComplete = status => status === SI_MENU_PAGESTATES.COMPLETE || status === SI_MENU_PAGESTATES.CURRENT_COMPLETE;
    const newMenu = addInitiation.menu.slice(0, staticMenuSize);

    markMenusComplete.forEach(menuIndex => {
      newMenu[menuIndex].pageState = SI_MENU_PAGESTATES.COMPLETE;
      newMenu[menuIndex].isFormUpdated = false;
    });

    newMenu.forEach(item => isMenuComplete(item.pageState && menusComplete++));

    if (menus[0] && menus[0].subMenu) {
      menus[0].subMenu.forEach(item => {
        if (!item.isDynamic) {
          newMenu.push({
            ...item,
            viewType: SI_ADDINITIATION_VIEWTYPES.DYNAMIC_CONTENT,
          });

          if (isMenuComplete(item.pageState)) menusComplete++;
        } else {
          // entity tables are nested inside submenu's so we must extract them out
          if (item.subMenu && item.subMenu.length) {
            item.subMenu.forEach(subItem => {
              newMenu.push({
                ...subItem,
                viewType: SI_ADDINITIATION_VIEWTYPES.DYNAMIC_CONTENT,
              });

              if (isMenuComplete(subItem.pageState)) menusComplete++;
            });
          }
        }
      });
    }

    const progress = Math.round((menusComplete * 100) / newMenu.length);
    return { menu: newMenu, progress };
  });
